<template>
    <div 
        class="carousel-container" 
        :class="{ mobile: isMobile, tablet: isTablet, medium: isMediumScreen }"
        @touchstart="startTouch"
        @touchmove="moveTouch"
        @touchend="endTouch"
    >
        <button class="arrow left" @click="prevSlide">❮</button>

        <div class="carousel-wrapper">
            <div 
                class="carousel"
                :style="carouselStyle"
            >
                <div 
                    class="carousel-slide"
                    v-for="(slide, slideIndex) in displayedItems" 
                    :key="slideIndex"
                    :class="{
                        'active': slideIndex === currentIndex, 
                        'side-left': slideIndex === currentIndex - 1, 
                        'side-right': slideIndex === currentIndex + 1
                    }"
                >
                    <TarjetaProducto
                        v-for="item in slide"
                        :key="item.id"
                        :menuItem="item"
                        :addToCart="addToCart"
                        :desactivar="desactivar"
                        :productos="productos"
                    />
                </div>
            </div>
        </div>

        <button class="arrow right" @click="nextSlide">❯</button>

        <div class="indicators">
            <span 
                v-for="(_, index) in totalSlides" 
                :key="index" 
                :class="{ active: index === currentIndex }"
                @click="goToSlide(index)"
            >
            </span>
        </div>
    </div>
</template>



<script>
    import TarjetaProducto from "./TarjetaProducto.vue";
    export default {
        props: {
            menuItems: {
                type: Array,
                required: true,
            },
            addToCart: {
                type: Function,
                required: true, 
            },
            desactivar: {
                type: Boolean,
                required: true,
            },
            productos: {
                type: Array,
                required: true,
            }
        },
        components: { TarjetaProducto },
        data() {
            return {
                currentIndex: 0,
                itemsPerSlide: 4,
                itemsPerSlideMedium: 3,
                itemsPerSlideTablet: 2,
                startX: 0,
                deltaX: 0,
                threshold: 50,
            };
        },
        computed: {
            isMobile() {
                return window.innerWidth <= 600;
            },
            isTablet() {
                return window.innerWidth > 600 && window.innerWidth <= 1079;
            },
            isMediumScreen() {
                return window.innerWidth >= 1080 && window.innerWidth <= 1209;
            },
            chunkedMenuItems() {
                let chunks = [];
                let itemsPerChunk;

                if (this.isMobile) {
                    itemsPerChunk = 1;
                } else if (this.isTablet) {
                    itemsPerChunk = this.itemsPerSlideTablet;
                } else if (this.isMediumScreen) {
                    itemsPerChunk = this.itemsPerSlideMedium;
                } else {
                    itemsPerChunk = this.itemsPerSlide;
                }
                for (let i = 0; i < this.menuItems.length; i += itemsPerChunk) {
                    chunks.push(this.menuItems.slice(i, i + itemsPerChunk));
                }
                return chunks.length ? chunks : [[]];
            },
            displayedItems() {
                return this.chunkedMenuItems;
            },
            totalSlides() {
                return this.displayedItems.length;
            },
            carouselStyle() {
                return {
                    transform: `translateX(calc(-${this.currentIndex * 100}%))`,
                    transition: "transform 0.5s ease-in-out"
                };
            }
        },
        watch: {
            menuItems: {
                handler(newItems) {
                    this.currentIndex = 0;
                },
                deep: true,
                immediate: true
            }
        },
        methods: {
            nextSlide() {
                if (this.currentIndex < this.totalSlides - 1) {
                    this.currentIndex++;
                }
            },
            prevSlide() {
                if (this.currentIndex > 0) {
                    this.currentIndex--;
                }
            },
            goToSlide(index) {
                this.currentIndex = index;
            },
            startTouch(event) {
                if (!this.isMobile  && !this.isTablet) return;
                this.startX = event.touches[0].clientX;
                this.deltaX = 0;
            },
            moveTouch(event) {
                if (!this.isMobile  && !this.isTablet) return;
                this.deltaX = event.touches[0].clientX - this.startX;
            },
            endTouch() {
                if (!this.isMobile  && !this.isTablet) return;
                if (this.deltaX > this.threshold && this.currentIndex > 0) {
                    this.currentIndex--;
                } else if (this.deltaX < -this.threshold && this.currentIndex < this.totalSlides - 1) {
                    this.currentIndex++;
                }
            }
        }
    };
</script>

<style scoped>
.carousel-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #ededed;
  padding: 20px;
  border-radius: 10px;
}

.carousel-wrapper {
  display: flex;
  overflow: hidden;
  width: 100%;
  padding: 0 10px;
}

.carousel {
  display: flex;
  transition: transform 0.5s ease-in-out;
  width: 100%;
}

.carousel-slide {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 20px;
  width: 100%;
  min-width: 100%;
  box-sizing: border-box;
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 12px;
  cursor: pointer;
  font-size: 20px;
  z-index: 10;
}

.left { left: 10px; }
.right { right: 10px; }

.arrow:hover {
  background: rgba(0, 0, 0, 0.8);
}

.indicators {
  text-align: center;
  margin-top: 10px;
}

.indicators span {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin: 5px;
  background: #bbb;
  border-radius: 50%;
  cursor: pointer;
}
.indicators .active {
  background: #333;
}

@media (min-width: 1210px) {
  .carousel-slide {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding: 20px;
    width: 100%;
  }
}

@media (min-width: 1080px) and (max-width: 1209px) {
  .carousel-slide {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    padding: 20px;
    width: 100%;
  }
}

@media (min-width: 601px) and (max-width: 1079px) {
  .carousel-slide {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 20px;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .carousel-slide {
    display: flex;
    flex: 0 0 60%;
    max-width: 60%;
    transition: opacity 0.5s ease, transform 0.5s ease;
  }

  .carousel-wrapper {
    padding: 0 40px;
  }

  .carousel-slide.active {
    opacity: 1;
    transform: scale(1) translateX(0%);
  }

  .carousel-slide.side-left {
    opacity: 0.7;
    transform: scale(0.9) translateX(-30%);
  }

  .carousel-slide.side-right {
    opacity: 0.7;
    transform: scale(0.9) translateX(30%);
  }
}

@media (max-width: 420px) {
  .carousel-wrapper {
    padding: 0 15px;
  }
}

</style>

